// 📄 6. src/components/Layout/ProtectedRoute.jsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { Navigate, useLocation } from 'react-router-dom';
import TrialExpiredModal from '../Subscription/TrialExpiredModal';
import { getProductLandingUrl, normalizeProductKey } from '../../utils/productRouting';
import { isDesktopRuntime, navigateOrRedirect } from '../../utils/desktopNavigation';
import { needsSignupQuestions } from '../../utils/signupQuestions';

const ProtectedRoute = ({ children, requireProfile = true }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { loading: onboardingLoading, profileComplete } = useOnboarding();
  const location = useLocation();
  const [trialCheckLoading, setTrialCheckLoading] = useState(true);
  const [trialBlocked, setTrialBlocked] = useState(false);
  const [trialBlockedTeamMember, setTrialBlockedTeamMember] = useState(false);
  const [hasResolvedTrialCheck, setHasResolvedTrialCheck] = useState(false);
  const [guardTimedOut, setGuardTimedOut] = useState(false);
  const [authParamWaitTimedOut, setAuthParamWaitTimedOut] = useState(false);

  // Check which app routes we're on
  const isAutopilotAppRoute = location.pathname.startsWith('/app/autopilot');
  const isDataIQAppRoute = location.pathname.startsWith('/app/data-iq');
  const isKnowledgeIQAppRoute = location.pathname.startsWith('/app/knowledge-iq');
  const isStandaloneProductRoute = isAutopilotAppRoute || isDataIQAppRoute || isKnowledgeIQAppRoute;

  // Get the current product context from localStorage
  // This is set by the landing page and indicates which product the user is trying to access
  const currentProduct = localStorage.getItem('currentProduct');
  const resolvedProduct = normalizeProductKey(currentProduct, location.pathname);

  // Determine product access for the authenticated user
  // IMPORTANT: Only use actual user data from the database (productAccess, signupProduct)
  // Do NOT grant access based on currentProduct from localStorage - that can be bypassed
  // 
  // NOTE: Intelligence Navigator users access Knowledge IQ, Data IQ, and Autopilot 
  // as MODULES within the /dashboard. They do NOT have access to standalone product apps.
  // Standalone apps are for users who subscribed ONLY to those individual products.
  const hasIntelligenceNavigatorAccess =
    user?.productAccess?.includes('intelligence-navigator') ||
    user?.signupProduct === 'intelligence-navigator';

  const hasAutopilotAccess =
    user?.productAccess?.includes('autopilot') || 
    user?.signupProduct === 'autopilot';
  
  const hasDataIQAccess =
    user?.productAccess?.includes('data-iq') || 
    user?.signupProduct === 'data-iq';
  
  const hasKnowledgeIQAccess =
    user?.productAccess?.includes('knowledge-iq') || 
    user?.signupProduct === 'knowledge-iq';

  // Profile is auto-completed on signup - no onboarding redirect needed

  // Determine which product the user is currently using (for trial guard & access checks)
  const activeProduct = isAutopilotAppRoute
    ? 'autopilot'
    : isDataIQAppRoute
      ? 'data-iq'
      : isKnowledgeIQAppRoute
        ? 'knowledge-iq'
        : 'intelligence-navigator';

  // Desktop packaged app can intermittently stall during guard bootstrapping.
  // For /dashboard route, keep rendering responsive by bypassing non-critical guard loaders.
  const hasAuthParam = Boolean(new URLSearchParams(window.location.search).get('auth'));
  const effectiveAuthLoading = authLoading && !guardTimedOut;
  const effectiveOnboardingLoading = onboardingLoading && !guardTimedOut;
  const effectiveTrialCheckLoading = trialCheckLoading && !guardTimedOut;
  const desktopGuardDebugEnabled = false;

  const redirectToProductLanding = (productKey) => {
    // In Electron, keep users inside the desktop shell and route to in-app landing.
    if (isDesktopRuntime) {
      if (productKey) {
        localStorage.setItem('currentProduct', productKey);
      }
      navigateOrRedirect('/', { replace: true });
      return;
    }

    window.location.href = getProductLandingUrl(productKey || resolvedProduct, location.pathname);
  };

  const renderGuardLoader = (label) => (
    <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
        {desktopGuardDebugEnabled && (
          <div className="mt-4 max-w-xl rounded-lg border border-blue-900/60 bg-[#0f172a]/80 px-3 py-2 text-left text-xs text-blue-100">
            <div className="font-semibold text-blue-200">Desktop Guard Diagnostics</div>
            <div>phase: {label}</div>
            <div>authLoading: {String(authLoading)}</div>
            <div>onboardingLoading: {String(onboardingLoading)}</div>
            <div>trialCheckLoading: {String(trialCheckLoading)}</div>
            <div>guardTimedOut: {String(guardTimedOut)}</div>
            <div>isAuthenticated: {String(isAuthenticated)}</div>
            <div>hasResolvedTrialCheck: {String(hasResolvedTrialCheck)}</div>
            <div>path: {location.pathname}</div>
          </div>
        )}
      </div>
    </div>
  );

  useEffect(() => {
    const anyGuardPending = authLoading || onboardingLoading || trialCheckLoading;
    if (!anyGuardPending) {
      setGuardTimedOut(false);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[ProtectedRoute] Guard timeout reached; continuing with safe fallback state.');
      }
      setGuardTimedOut(true);
      setTrialCheckLoading(false);
      setHasResolvedTrialCheck(true);
    }, 8000);

    return () => clearTimeout(timeoutId);
  }, [authLoading, onboardingLoading, trialCheckLoading]);

  useEffect(() => {
    let cancelled = false;
    const checkTrialGuard = async () => {
      // Keep guard loading while auth bootstrap is in-flight to avoid
      // rendering dashboard content before trial/subscription checks begin.
      if (effectiveAuthLoading) {
        return;
      }

      if (!isAuthenticated) {
        if (!cancelled) {
          setTrialCheckLoading(false);
          setHasResolvedTrialCheck(false);
        }
        return;
      }

      const shouldCheck = activeProduct === 'autopilot' || activeProduct === 'intelligence-navigator';
      const isInSubscriptionRoute = location.pathname.startsWith('/subscription');
      if (!shouldCheck || isInSubscriptionRoute) {
        if (!cancelled) {
          setTrialBlocked(false);
          setTrialBlockedTeamMember(false);
          setTrialCheckLoading(false);
          setHasResolvedTrialCheck(true);
        }
        return;
      }

      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        if (!cancelled) {
          setTrialCheckLoading(false);
          setHasResolvedTrialCheck(true);
        }
        return;
      }

      // Only block route rendering the first time.
      // After first successful resolution, run checks in background to avoid page flashes.
      if (!cancelled && !hasResolvedTrialCheck) setTrialCheckLoading(true);
      const resolveCurrentSubscriptionActive = async (apiBaseUrl, authToken, productKey) => {
        try {
          const currentRes = await fetch(
            `${apiBaseUrl}/subscriptions/current?product=${encodeURIComponent(productKey)}`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
          if (!currentRes.ok) {
            return null;
          }
          const currentPayload = await currentRes.json();
          const currentSubscription = currentPayload?.subscription || null;
          if (!currentSubscription) {
            return null;
          }

          const now = Date.now();
          const currentStatus = currentSubscription?.status;
          const trialEndAt = currentSubscription?.trialEndDate
            ? new Date(currentSubscription.trialEndDate).getTime()
            : null;
          const trialStillValid = !Number.isFinite(trialEndAt) || trialEndAt >= now;
          const hasPaidSignal = Boolean(currentSubscription?.stripeSubscriptionId);
          const isFreeOrManualPlan = currentSubscription?.plan === 'free' || currentSubscription?.plan === 'enterprise';

          return currentStatus === 'past_due'
            || (currentStatus === 'active' && (hasPaidSignal || isFreeOrManualPlan || trialStillValid))
            || (currentStatus === 'trialing' && trialStillValid);
        } catch {
          return null;
        }
      };

      try {
        const apiBase = process.env.REACT_APP_API_URL || 'https://api.xpherium.com/api';
        const response = await fetch(`${apiBase}/subscriptions/status/${activeProduct}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const error = new Error(`status check failed: ${response.status}`);
          error.statusCode = response.status;
          throw error;
        }
        const raw = await response.json();
        const data = raw?.subscriptionStatus && typeof raw.subscriptionStatus === 'object'
          ? raw.subscriptionStatus
          : raw;
        const subscription = data?.subscription || {};
        const status = subscription?.status;
        const apiIsActive = data?.isActive;
        const apiTrialExpired = Boolean(data?.isTrialExpired);
        const apiHasSubscription = typeof data?.hasSubscription === 'boolean'
          ? data.hasSubscription
          : null;

        // Align to backend/web source-of-truth contract.
        // Prefer explicit isActive from backend and only infer from status when missing.
        const computedIsActive = typeof apiIsActive === 'boolean'
          ? apiIsActive
          : (['trialing', 'active'].includes(status) && !apiTrialExpired);

        // Fail closed for explicit expiration/inactive states, but do not let
        // a missing/misaligned hasSubscription flag override an explicit active state.
        let shouldBlock = apiTrialExpired
          || computedIsActive === false
          || (apiHasSubscription === false && computedIsActive !== true);

        // Secondary verification: when status says "blocked", confirm against
        // current subscription record to avoid false negatives from status shape drift.
        if (shouldBlock) {
          const currentIsActive = await resolveCurrentSubscriptionActive(apiBase, token, activeProduct);
          if (currentIsActive === true) {
            shouldBlock = false;
          }
        }

        if (!cancelled) {
          setTrialBlocked(Boolean(shouldBlock));
          setTrialBlockedTeamMember(Boolean(shouldBlock && data?.isTeamMember));
          setHasResolvedTrialCheck(true);
        }
      } catch (err) {
        // Fail closed by default, but verify one more backend source before blocking.
        const apiBase = process.env.REACT_APP_API_URL || 'https://api.xpherium.com/api';
        const currentIsActive = await resolveCurrentSubscriptionActive(apiBase, token, activeProduct);
        if (!cancelled) {
          const shouldBlock = currentIsActive === true ? false : true;
          setTrialBlocked(shouldBlock);
          setTrialBlockedTeamMember(false);
          setHasResolvedTrialCheck(true);
        }
      } finally {
        if (!cancelled && !hasResolvedTrialCheck) setTrialCheckLoading(false);
      }
    };

    checkTrialGuard();
    return () => { cancelled = true; };
  }, [effectiveAuthLoading, isAuthenticated, activeProduct, location.pathname, hasResolvedTrialCheck]);

  useEffect(() => {
    if (isAuthenticated || authLoading || !hasAuthParam) {
      setAuthParamWaitTimedOut(false);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[ProtectedRoute] Auth param processing timed out; clearing stale auth query.');
      }
      setAuthParamWaitTimedOut(true);
    }, 3500);

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, authLoading, hasAuthParam]);

  // (Onboarding redirect removed - profile is auto-completed on signup)

  // Show loading while checking auth status
  if (effectiveAuthLoading || effectiveOnboardingLoading) {
    return renderGuardLoader('auth-or-onboarding');
  }

  // Not authenticated - check if we should wait or redirect
  if (!isAuthenticated) {
    if (hasAuthParam && !authParamWaitTimedOut) {
      // Auth param in URL - authentication is being processed, wait for re-render
      if (process.env.NODE_ENV !== 'production') {
        console.log('🔐 ProtectedRoute: Processing auth from URL...');
      }
      return renderGuardLoader('auth-param-processing');
    }

    if (hasAuthParam && authParamWaitTimedOut) {
      try {
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.delete('auth');
        nextUrl.searchParams.delete('returnTo');
        window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      } catch {
        // Ignore cleanup errors and continue with redirect fallback.
      }
    }

    // CRITICAL FAILSAFE (May 27 2026 sign-in bounce-back regression).
    //
    // If a token is present in localStorage but `user` is null, we are
    // very likely mid-validation (e.g. /auth/me is in flight, returned a
    // transient empty body, hit a CORS hiccup, etc.). Hard-bouncing to
    // www.xpherium.com/intelligence-navigator at this moment is what made
    // sign-in look "broken" — the dashboard would render the auth-loading
    // spinner, then immediately redirect outside the app on the very next
    // tick when AuthContext.user briefly fell back to null. AuthContext
    // already handles genuine 401/403 by *clearing* the token first; if
    // the token is still here, we have not concluded the session is
    // dead, so we must not throw the visitor off the app.
    //
    // We instead navigate to the dashboard's OWN unauthenticated entry
    // page (`/`, which renders the in-app sign-in form), so the visitor
    // sees a familiar Xpherium screen and can re-authenticate without
    // losing their tab/history state. If the AuthContext eventually
    // resolves the token successfully, the LandingPage's auth-redirect
    // effect will quietly forward them back to /dashboard. If it doesn't,
    // they have a one-click way back into the app — no external bounce.
    let hasLocalAuthToken = false;
    try {
      hasLocalAuthToken = Boolean(
        localStorage.getItem('token') || localStorage.getItem('auth_token')
      );
    } catch {
      // Storage access denied — fall through to the existing redirect.
    }

    if (hasLocalAuthToken) {
      // Always log this — even in production — so visitors stuck in this
      // failsafe path can hand us a precise console snapshot. The bounce
      // we are protecting against is silent in normal browser activity;
      // the only reliable signal is this console line + the storage
      // dump below.
      try {
        const storedUser = localStorage.getItem('user');
        const currentProduct = localStorage.getItem('currentProduct');
        // eslint-disable-next-line no-console
        console.warn(
          '[XPH-DEBUG-AUTH] ProtectedRoute failsafe: token present but user not yet hydrated. Soft-navigating to "/" instead of marketing redirect.',
          {
            path: location.pathname,
            authParam: window.location.search.includes('auth=') ? 'present' : 'absent',
            authLoading,
            hasLocalAuthToken,
            storedUserPresent: Boolean(storedUser),
            storedUserPreview: storedUser ? storedUser.slice(0, 80) + '…' : null,
            currentProduct,
            authParamWaitTimedOut,
          }
        );
      } catch {
        // never let logging break the failsafe
      }
      // Use react-router so it's a soft navigation; LandingPage will
      // forward back to /dashboard once user state hydrates.
      return <Navigate to="/" replace />;
    }

    // No auth data at all - redirect to Next.js product landing page
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔐 ProtectedRoute: Not authenticated, redirecting to landing page', { currentProduct });
    }

    redirectToProductLanding(resolvedProduct);
    return null;
  }

  // Account-type guard. Pure 'partner' accounts (affiliate-only, no IN
  // subscription) have no business being inside the IN dashboard, even
  // if they manage to construct a URL like /dashboard. Bounce them to
  // their own portal. Customer accounts pass through untouched. The
  // /partner-portal route uses PartnerRoute, not this guard, so partners
  // can still access their portal.
  if (user?.accountType === 'partner') {
    return <Navigate to="/partner-portal" replace />;
  }

  // First-run signup questions (Supademo-style, Aug 2026). Every new
  // customer account — free or paid, email/Google/SSO — answers the
  // /welcome questionnaire exactly once before seeing the dashboard.
  // Completion is stored on the Client row (companyProfile.onboarding
  // + settings.onboardingQuestionsCompleted), so logout / a new browser
  // cannot replay it. Existing accounts never have the required flag.
  const mustAnswerSignupQuestions = needsSignupQuestions(user);
  const isWelcomeRoute = location.pathname.startsWith('/welcome');
  if (mustAnswerSignupQuestions && !isWelcomeRoute && !isStandaloneProductRoute) {
    return <Navigate to="/welcome" replace />;
  }
  if (!mustAnswerSignupQuestions && isWelcomeRoute) {
    return <Navigate to="/dashboard" replace />;
  }

  const requiresProfileCompletion = Boolean(user?.settings?.profileCompletionRequired) && !profileComplete;
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  if (requiresProfileCompletion && !isOnboardingRoute) {
    const onboardingProduct = isAutopilotAppRoute
      ? 'autopilot'
      : isDataIQAppRoute
        ? 'data-iq'
        : isKnowledgeIQAppRoute
          ? 'knowledge-iq'
          : 'intelligence-navigator';
    return <Navigate to={`/onboarding?product=${encodeURIComponent(onboardingProduct)}`} replace />;
  }

  // Helper: wrap children with the trial-expired guard
  const withTrialGuard = (content) => (
    <>
      <TrialExpiredModal product={activeProduct} />
      {content}
    </>
  );

  // Enforce product boundaries once profile is complete
  
  // Allow access to standalone product apps if user has access
  if (isAutopilotAppRoute && hasAutopilotAccess) {
    return withTrialGuard(children);
  }
  if (isDataIQAppRoute && hasDataIQAccess) {
    return withTrialGuard(children);
  }
  if (isKnowledgeIQAppRoute && hasKnowledgeIQAccess) {
    return withTrialGuard(children);
  }

  // Deny access to standalone product apps if user doesn't have access
  // Redirect to Next.js product landing page to sign up
  if (isAutopilotAppRoute && !hasAutopilotAccess) {
    redirectToProductLanding('autopilot');
    return null;
  }
  if (isDataIQAppRoute && !hasDataIQAccess) {
    redirectToProductLanding('data-iq');
    return null;
  }
  if (isKnowledgeIQAppRoute && !hasKnowledgeIQAccess) {
    redirectToProductLanding('knowledge-iq');
    return null;
  }

  // Hard route-level trial enforcement for products with no free tier.
  if ((activeProduct === 'autopilot' || activeProduct === 'intelligence-navigator') && effectiveTrialCheckLoading) {
    return renderGuardLoader('trial-check');
  }

  if (trialBlocked && activeProduct === 'intelligence-navigator') {
    // Keep users in-app and let TrialExpiredModal enforce blocking.
    // This prevents false-positive hard redirects when backend status payloads are delayed/inconsistent.
    return withTrialGuard(children);
  }

  if (trialBlocked && activeProduct === 'autopilot') {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 text-center">
          <h2 className="text-xl font-semibold text-white mb-3">
            {trialBlockedTeamMember ? 'Service Momentarily Interrupted' : 'Your Agentic AI trial has ended'}
          </h2>
          <p className="text-slate-400 text-sm mb-6">
            {trialBlockedTeamMember
              ? 'Access is temporarily unavailable. Please contact your administrator.'
              : 'Upgrade your subscription to restore access.'}
          </p>
          {!trialBlockedTeamMember && (
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-subscription', { detail: { product: 'autopilot', trialExpired: true } }))}
              className="w-full py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
            >
              Upgrade Now
            </button>
          )}
        </div>
      </div>
    );
  }

  // For non-standalone routes (like /dashboard), check if user has Intelligence Navigator access
  if (!isStandaloneProductRoute && !hasIntelligenceNavigatorAccess) {
    // User doesn't have IN access - redirect based on currentProduct first, then productAccess
    if (currentProduct === 'autopilot') {
      return <Navigate to="/app/autopilot" replace />;
    }
    if (currentProduct === 'data-iq') {
      return <Navigate to="/app/data-iq" replace />;
    }
    if (currentProduct === 'knowledge-iq') {
      return <Navigate to="/app/knowledge-iq" replace />;
    }
    // No current product context - check user's product access
    if (user?.productAccess?.includes('autopilot')) {
      return <Navigate to="/app/autopilot" replace />;
    }
    if (user?.productAccess?.includes('data-iq')) {
      return <Navigate to="/app/data-iq" replace />;
    }
    if (user?.productAccess?.includes('knowledge-iq')) {
      return <Navigate to="/app/knowledge-iq" replace />;
    }
    // No product access at all - redirect to main landing
    return <Navigate to="/" replace />;
  }

  return withTrialGuard(children);
};

export default ProtectedRoute;